/**
 * Wait Step Executor — Delay or wait for a condition
 *
 * Config:
 * - delayMs: number — milliseconds to wait
 * - maxWaitMs?: number — maximum wait time for condition polling (default 60000)
 * - pollIntervalMs?: number — interval to check condition (default 1000)
 * - condition?: { field, operator, value } — optional condition to poll for
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';

export const waitStepExecutor: StepExecutor = {
  type: 'wait',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    try {
      const delayMs = step.config.delayMs as number;
      const condition = step.config.condition as { field: string; operator: string; value: unknown } | undefined;

      // Simple delay
      if (delayMs && !condition) {
        await sleep(Math.min(delayMs, 300000)); // Max 5 minutes
        return {
          success: true,
          output: { waited: delayMs },
        };
      }

      // Condition polling
      if (condition) {
        const maxWait = (step.config.maxWaitMs as number) || 60000;
        const pollInterval = (step.config.pollIntervalMs as number) || 1000;
        const startTime = Date.now();

        while (Date.now() - startTime < maxWait) {
          const fieldValue = resolveDataPath(condition.field, context.pipelineData);
          if (evaluateSimple(fieldValue, condition.operator, condition.value)) {
            return {
              success: true,
              output: { conditionMet: true, waitedMs: Date.now() - startTime },
            };
          }
          await sleep(pollInterval);
        }

        return {
          success: false,
          error: `Wait condition not met within ${maxWait}ms`,
          output: { conditionMet: false, waitedMs: maxWait },
        };
      }

      // Default: no delay, no condition
      return { success: true, output: { waited: 0 } };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Wait step failed',
      };
    }
  },
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveDataPath(path: string, data: Record<string, unknown>): unknown {
  const parts = path.split('.');
  let current: unknown = data;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function evaluateSimple(fieldValue: unknown, operator: string, value: unknown): boolean {
  switch (operator) {
    case 'eq': return fieldValue === value;
    case 'neq': return fieldValue !== value;
    case 'exists': return fieldValue !== undefined && fieldValue !== null;
    case 'gt': return typeof fieldValue === 'number' && typeof value === 'number' ? fieldValue > value : false;
    case 'lt': return typeof fieldValue === 'number' && typeof value === 'number' ? fieldValue < value : false;
    default: return false;
  }
}
